<template>
  <DialogWrapper
    class="question-edit-dialog"
    :show.sync="innerShow"
    :title="dialogTitle"
    :loading="isLoading"
    :width="isOptionType ? '680px' : '720px'"
    :top="isOptionType ? '2vh' : '8vh'"
    :disabledSaveBtn="disabledSaveBtn"
    :hideFooter="readonly"
    :closeOnClickModal="false"
    @save="handleSave"
  >
    <div
      v-if="isQuestionLocked"
      v-auto-tooltip.always="$t(`${i18nPrefix}.message.lockedQuestionnaire`)"
      class="locked-tag"
    >
      {{ $t(`${i18nPrefix}.label.locked`) }}
    </div>
    <WrappedForm
      ref="form"
      label-position="top"
      :model="formData"
      :formLines="formLines"
    >
      <template #optionContent="{ lineName, line }">
        <div class="option-input-tip">{{ line.tipLabel }}</div>
          <el-input
            v-model="formData[lineName]"
            v-bind="line.props"
            :placeholder="line.placeholder"
            :disabled="line.disabled || isQuestionReadonly"
          />
      </template>
    </WrappedForm>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { HM_QUESTION_STATUS, HM_QUESTION_TYPE } from '@/constant/questionnaire'
import { QUESTION_TYPE_OPTIONS, isOptionQuestion } from './questionType'

export default {
  components: {
    DialogWrapper,
    WrappedForm
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    question: {
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.company.questionnaireManagement',
      saving: false,
      formData: {
        type: HM_QUESTION_TYPE.RATING,
        titleCn: null,
        titleEn: null,
        optionsCn: null,
        optionsEn: null,
        status: HM_QUESTION_STATUS.ENABLED
      }
    }
  },
  computed: {
    isLoading () {
      return this.loading || this.saving
    },
    innerShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    dialogTitle () {
      if (this.readonly) {
        return this.$t(`${this.i18nPrefix}.dialog.viewQuestion`)
      }
      return this.$t(`${this.i18nPrefix}.dialog.${this.isEdit ? 'editQuestion' : 'addQuestion'}`)
    },
    isOptionType () {
      return isOptionQuestion(this.formData.type)
    },
    isQuestionLocked () {
      return this.isEdit && (this.formData.isLocked || this.formData.isInherited)
    },
    isQuestionReadonly () {
      return this.readonly || this.isQuestionLocked
    },
    disabledSaveBtn () {
      if (this.readonly) {
        return true
      }
      if (!this.formData.type || !this.formData.titleCn || !this.formData.titleEn) {
        return true
      }
      if (this.isOptionType) {
        return !this.formData.optionsCn || !this.formData.optionsEn
      }
      return false
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        type: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          disabled: this.isEdit || this.readonly,
          option: {
            data: _.map(QUESTION_TYPE_OPTIONS, item => ({
              value: item.value,
              label: this.$t(item.labelI18nKey)
            }))
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionType`),
          rules: [{ required: true, message: requiredMessage, trigger: 'change' }]
        },
        titleCn: {
          label: this.$t(`${this.i18nPrefix}.label.chineseTitle`),
          disabled: this.isQuestionReadonly,
          props: { type: 'textarea', autosize: { minRows: 4 } },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        titleEn: {
          label: this.$t(`${this.i18nPrefix}.label.englishTitle`),
          disabled: this.isQuestionReadonly,
          props: { type: 'textarea', autosize: { minRows: 4 } },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        optionsCn: {
          label: this.$t(`${this.i18nPrefix}.label.chineseOptions`),
          contentSlot: 'optionContent',
          hidden: !this.isOptionType,
          disabled: this.isQuestionReadonly,
          props: { type: 'textarea', autosize: { minRows: 4 } },
          tipLabel: this.$t(`${this.i18nPrefix}.placeholder.options`),
          rules: [{ required: this.isOptionType, message: requiredMessage, trigger: 'blur' }]
        },
        optionsEn: {
          label: this.$t(`${this.i18nPrefix}.label.englishOptions`),
          contentSlot: 'optionContent',
          hidden: !this.isOptionType,
          disabled: this.isQuestionReadonly,
          props: { type: 'textarea', autosize: { minRows: 4 } },
          tipLabel: this.$t(`${this.i18nPrefix}.placeholder.options`),
          rules: [{ required: this.isOptionType, message: requiredMessage, trigger: 'blur' }]
        }
      }
    }
  },
  methods: {
    ...mapActions('questionnaire', ['createHmQuestion', 'updateHmQuestion']),
    splitOptions (text) {
      return _.chain((text || '').split('\n'))
        .map(item => item.trim())
        .filter()
        .value()
    },
    buildQuestionOptionPayload () {
      const cnOptions = this.splitOptions(this.formData.optionsCn)
      const enOptions = this.splitOptions(this.formData.optionsEn)
      const length = Math.max(cnOptions.length, enOptions.length)
      return _.chain([])
        .thru(() => _.range(0, length))
        .map(index => ({
          label_zh: cnOptions[index] || enOptions[index],
          label_en: enOptions[index],
          sort_order: index + 1,
          status: HM_QUESTION_STATUS.ENABLED
        }))
        .value()
    },
    buildQuestionPayload () {
      const payload = {
        question_type: this.formData.type,
        title_zh: this.formData.titleCn,
        title_en: this.formData.titleEn,
        status: this.formData.status || HM_QUESTION_STATUS.ENABLED
      }
      if (_.includes([HM_QUESTION_TYPE.SINGLE, HM_QUESTION_TYPE.MULTIPLE], this.formData.type)) {
        payload.options = this.buildQuestionOptionPayload()
      }
      return payload
    },
    createEmptyQuestion () {
      return {
        type: HM_QUESTION_TYPE.RATING,
        titleCn: null,
        titleEn: null,
        optionsCn: null,
        optionsEn: null,
        status: HM_QUESTION_STATUS.ENABLED
      }
    },
    syncFormData () {
      this.formData = this.question ? _.cloneDeep(this.question) : this.createEmptyQuestion()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    async handleSave () {
      if (this.isQuestionReadonly) {
        this.innerShow = false
        return
      }
      const valid = await this.$refs.form.submit()
      if (!valid || this.disabledSaveBtn) {
        return
      }
      this.saving = true
      try {
        const payload = this.buildQuestionPayload()
        if (this.formData.id) {
          await this.updateHmQuestion({ id: this.formData.id, payload })
        } else {
          payload.status = HM_QUESTION_STATUS.ENABLED
          await this.createHmQuestion(payload)
        }
        this.$message.success(this.$t('common.message.saveSuccess'))
        this.innerShow = false
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    }
  },
  watch: {
    show: {
      immediate: true,
      handler (val) {
        if (!val) {
          return
        }
        this.syncFormData()
      }
    },
    question: {
      deep: true,
      handler () {
        if (!this.show) {
          return
        }
        this.syncFormData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.question-edit-dialog {
  .locked-tag {
    display: inline;
    border: 1px solid #d95f63;
    padding: 4px 8px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 500;
    color: #d95f63;
    line-height: 10px;
    background: #d95f631a;
    white-space: normal;
  }

  ::v-deep {
    .form-container {
      .el-form-item {
        margin-bottom: 20px;
      }

      .el-form-item__label {
        line-height: 32px;
        font-weight: 500;
        white-space: normal;
      }

      .option-input-tip {
        color: #909399;
        font-size: 14px;
        line-height: 1;
        white-space: normal;
        margin-bottom: 2px;
      }

      .el-input.is-disabled {
        .el-input__inner,
        .el-textarea__inner {
          color: #b8c0cc;
          background: #f5f7fa;
          border-color: #dcdfe6;
        }
      }
    }
  }
}
</style>
